'use client';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { Coords } from '@witmetrics/utils';
import { isEmpty } from '@witmetrics/utils/misc';
import { createImage } from '@/utils/dom';
import type { DrawingCursor, DrawingPath, DrawingType } from './types';
import { DEFAULT_SIZE, DEFAULT_TYPE, DRAWING_TYPE } from './constants';
import { getPathColor } from './utils';

export { DRAWING_TYPE };

export type { DrawingCursor, DrawingPath, DrawingType };

const { POLYGON, ERASER } = DRAWING_TYPE;

type CanvasDrawingValue = {
  cursor: DrawingCursor;
  getCanvas: () => HTMLCanvasElement | null;
  setCanvas: (canvas: HTMLCanvasElement) => void;
  captureMask: () => Promise<HTMLImageElement>;
  updateCursor: (cursor: Partial<DrawingCursor>) => void;
  moveCursor: (coords: Coords | null) => void;
  getPath: (index?: number) => DrawingPath;
  addPath: (path: DrawingPath) => void;
  updatePath: (path: DrawingPath, index?: number) => void;
  clearPaths: () => void;
  renderCanvas: () => void;
};

const DrawingCanvasContext = createContext<CanvasDrawingValue | undefined>(
  undefined
);

export default function DrawingCanvasProvider({ children }: PropsWithChildren) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctx = useRef<CanvasRenderingContext2D | null>(null);
  const lastCoords = useRef<Coords | null>(null);
  const paths = useRef<DrawingPath[]>([
    {
      type: DEFAULT_TYPE,
      size: DEFAULT_SIZE,
      path: [],
    },
  ]);
  const [cursor, setCursor] = useState<DrawingCursor>({
    disabled: false,
    type: DEFAULT_TYPE,
    size: DEFAULT_SIZE,
  });

  useEffect(() => {
    // Re-render the canvas whenever the cursor changes
    renderCanvas();
  }, [cursor]);

  const getCanvas = () => canvasRef.current;

  const setCanvas = (canvas: HTMLCanvasElement) => {
    canvasRef.current = canvas;
    ctx.current = canvasRef.current.getContext('2d');
    if (!ctx.current) {
      throw new Error('Unable to establish CanvasRenderingContext2D');
    }
  };

  const updateCursor = (update: Partial<DrawingCursor>) => {
    setCursor((c) => ({ ...c, ...update }));
  };

  const moveCursor = (coords: Coords | null) => {
    lastCoords.current = coords;
    renderCanvas();
  };

  const getPath = (index?: number) => {
    return paths.current[
      index === undefined ? paths.current.length - 1 : index
    ];
  };

  const addPath = (path: DrawingPath) => {
    paths.current.push(path);
  };

  const updatePath = (updates: Partial<DrawingPath>, index?: number) => {
    // NOTE: If paths is empty or index is out of bounds, add as new path
    const key = index === undefined ? paths.current.length - 1 : index;
    if (isEmpty(paths.current) || !paths.current[key]) {
      return addPath({
        type: updates.type || cursor.type,
        size: updates.size || cursor.size,
        path: updates.path || [],
      });
    } else {
      paths.current[key] = { ...paths.current[key], ...updates };
    }
  };

  const clearPaths = () => {
    paths.current = [];
  };

  const captureMask = () => {
    if (!canvasRef.current || !ctx.current) {
      throw Error('HTMLCanvasElement or CanvasRenderingContext2D is undefined');
    }
    const { width, height } = canvasRef.current;
    ctx.current.clearRect(0, 0, width, height);
    if (paths.current.length > 0) renderPaths(true);
    return createImage(canvasRef.current.toDataURL('image/png'));
  };

  const renderCanvas = () => {
    if (!canvasRef.current || !ctx.current) {
      return console.warn(
        'HTMLCanvasElement or CanvasRenderingContext2D is undefined'
      );
    }
    const { width, height, clientWidth, clientHeight } = canvasRef.current;
    if (width !== clientWidth || height !== clientHeight) {
      canvasRef.current.width = canvasRef.current.clientWidth;
      canvasRef.current.height = canvasRef.current.clientHeight;
    }
    ctx.current.clearRect(0, 0, width, height);
    if (paths.current.length > 0) renderPaths(false);
    if (lastCoords.current) renderCursor(false);
  };

  const renderPaths = (isCaptureMode: boolean) => {
    paths.current.forEach(({ path, type, size }, index) => {
      // Iterate over each drawing path stored in state
      if (isEmpty(path)) return; // Path is empty, don't worry about the rest
      const isRegion = type === POLYGON;
      const isLastPath = index === paths.current.length - 1;

      ctx.current!.moveTo(path[0][0], path[0][1]);
      ctx.current!.beginPath();
      ctx.current!.lineWidth = isRegion ? 2 : size;
      ctx.current!.strokeStyle = getPathColor(
        isRegion ? 1 : 0.5,
        isCaptureMode
      );
      ctx.current!.fillStyle = getPathColor(0.5, isCaptureMode);
      ctx.current!.lineJoin = 'round';
      if (type === ERASER) {
        ctx.current!.save();
        ctx.current!.globalCompositeOperation = 'destination-out';
        ctx.current!.strokeStyle = 'black';
      } else if (isRegion && isLastPath) {
        ctx.current!.globalCompositeOperation = 'source-over';
      } else ctx.current!.globalCompositeOperation = 'xor';

      for (let i = 1; i < path.length; i++) {
        // Close off the regions
        if (isRegion && isLastPath) {
          ctx.current!.moveTo(path[i - 1][0], path[i - 1][1]);
        }
        ctx.current!.lineTo(path[i][0], path[i][1]);
      }

      if (!isRegion) {
        ctx.current!.stroke();
        ctx.current!.closePath();
        if (type === ERASER) ctx.current!.restore();
      } else if (isLastPath) {
        ctx.current!.closePath();
        ctx.current!.stroke();
      } else {
        ctx.current!.lineTo(path[0][0], path[0][1]);
        ctx.current!.closePath();
        ctx.current!.fill();
      }
    });
    ctx.current!.restore();
  };

  const renderCursor = (isCaptureMode: boolean) => {
    if (!ctx.current || !lastCoords.current || cursor.disabled) {
      return null;
    }
    const { type, size } = cursor;
    const color = getPathColor(0.6, isCaptureMode);
    const pathSize = type === POLYGON ? 4 : 0.5 * size;
    const [x, y] = lastCoords.current;
    ctx.current.moveTo(x, y);
    ctx.current.beginPath();
    ctx.current.arc(x, y, pathSize, 0, Math.PI * 2, true);
    ctx.current.globalCompositeOperation = 'source-over';
    if (type === ERASER) {
      ctx.current.lineWidth = 2;
      ctx.current.strokeStyle = color;
      ctx.current.fillStyle = 'white';
      ctx.current.stroke();
    } else {
      ctx.current.lineWidth = 0;
      ctx.current.fillStyle = color;
    }
    ctx.current.fill();
  };

  const contextValue = {
    cursor,
    getCanvas,
    setCanvas,
    captureMask,
    updateCursor,
    moveCursor,
    getPath,
    addPath,
    updatePath,
    clearPaths,
    renderCanvas,
  };

  return (
    <DrawingCanvasContext.Provider value={contextValue}>
      {children}
    </DrawingCanvasContext.Provider>
  );
}

export function useDrawingCanvas() {
  const context = useContext(DrawingCanvasContext);
  if (!context) {
    throw Error('useDrawingCanvas must be used within a DrawingCanvasProvider');
  }
  return context;
}
